// src/composables/useConferences.js
import { ref, computed } from 'vue'
import { useConferencesStore } from '@/stores/conferences'
import { getCourseColor, normalizeId } from '@/utils/conferenceColors'

/**
 * Парсинг даты как локального времени
 */
function parseLocalDate(dateStr) {
	if (!dateStr) return new Date(NaN)
	if (/[Z+-]\d{2}:?\d{2}$/.test(dateStr) || dateStr.endsWith('Z')) {
		return new Date(dateStr)
	}
	const [datePart, timePart] = dateStr.split('T')
	if (!datePart) return new Date(NaN)
	const [year, month, day] = datePart.split('-').map(Number)
	const [hour = 0, minute = 0, second = 0] = (timePart || '00:00:00')
		.split(':')
		.map(Number)
	return new Date(year, month - 1, day, hour, minute, second)
}

/**
 * Composable для работы с конференциями студента
 */
export function useStudentConferences(enrolledCourseIds) {
	const conferences = ref([])
	const loading = ref(false)
	const includePast = ref(false)

	/**
	 * Конференции с цветами
	 */
	const conferencesWithColors = computed(() => {
		const seen = new Set()
		const unique = []

		for (const conf of conferences.value) {
			const confId = normalizeId(conf.id)
			if (confId !== null && !seen.has(confId)) {
				seen.add(confId)

				const courseId = normalizeId(
					conf.course_id || conf.courseId || conf.course?.id
				)

				unique.push({
					...conf,
					id: confId,
					courseId,
					course_id: courseId,
					courseTitle: conf.course_title || conf.course?.title || 'Курс',
					course_title: conf.course_title || conf.course?.title || 'Курс',
					color: getCourseColor(courseId),
				})
			}
		}

		return unique
	})

	/**
	 * Группировка по курсам
	 */
	const groupedConferences = computed(() => {
		const groups = {}

		conferences.value.forEach(conf => {
			const courseId = conf.course_id || conf.courseId || conf.course?.id
			const courseTitle = conf.course_title || conf.course?.title || 'Курс'
			const courseCategory = conf.course_category || conf.course?.category || null

			if (!courseId) return

			if (!groups[courseId]) {
				groups[courseId] = {
					course_id: courseId,
					course_title: courseTitle,
					course_category: courseCategory,
					conferences: [],
				}
			}
			groups[courseId].conferences.push(conf)
		})

		return Object.values(groups)
	})

	/**
	 * Загрузка конференций
	 */
	async function loadConferences() {
		loading.value = true

		try {
			const conferencesStore = useConferencesStore()

			await conferencesStore.fetchStudentConferences({
				force: true,
				includePast: true, // Всегда запрашиваем все, фильтруем на клиенте
			})

			const allConferences = conferencesStore.studentConferences || []
			const now = Date.now()

			// Хелпер: проверка "идёт сейчас"
			const isLiveNow = conf => {
				if (conf.status === 'live') return true
				const scheduled = parseLocalDate(conf.scheduled_at).getTime()
				const duration = (conf.duration_minutes || 60) * 60 * 1000
				return now >= scheduled && now <= scheduled + duration
			}

			// Хелпер: проверка "завершена"
			const isEnded = conf => {
				if (conf.status === 'completed' || conf.status === 'ended') return true
				if (conf.status === 'cancelled') return true
				const scheduled = parseLocalDate(conf.scheduled_at).getTime()
				const duration = (conf.duration_minutes || 60) * 60 * 1000
				return now > scheduled + duration
			}

			let rawConferences

			if (includePast.value) {
				// "Показать прошедшие" - ТОЛЬКО завершённые
				rawConferences = allConferences.filter(conf => isEnded(conf))
			} else {
				// Обычный режим - live + будущие
				rawConferences = allConferences.filter(conf => {
					if (isLiveNow(conf)) return true
					if (isEnded(conf)) return false
					// Будущие
					const scheduled = parseLocalDate(conf.scheduled_at).getTime()
					return scheduled > now
				})
			}

			// Фильтрация по курсам студента
			const enrolledIds = new Set(enrolledCourseIds?.value || [])

			conferences.value = (rawConferences || [])
				.filter(conf => {
					const confCourseId = normalizeId(conf.course_id || conf.course?.id)
					if (enrolledIds.size === 0) return true
					return confCourseId !== null && enrolledIds.has(confCourseId)
				})
				.map(conf => ({
					...conf,
					course_id: normalizeId(conf.course_id || conf.course?.id),
					course_title: conf.course_title || conf.course?.title || 'Курс',
					course_category: conf.course_category || conf.course?.category || null,
				}))

			console.log('✅ [useConferences] Загружено:', conferences.value.length)
		} catch (error) {
			console.error('❌ [useConferences] Ошибка:', error)
			conferences.value = []
		} finally {
			loading.value = false
		}
	}

	/**
	 * Переключение показа прошедших
	 */
	function togglePast(value) {
		includePast.value = value
		loadConferences()
	}

	/**
	 * Присоединение к конференции
	 * @param {Object} conf - объект конференции
	 * @param {Object} router - Vue Router instance
	 */
	async function joinConference(conf, router) {
		const conferenceId = normalizeId(conf.id)

		if (!conferenceId) {
			console.error('❌ [joinConference] Невалидный ID конференции:', conf)
			throw new Error('Невалидный ID конференции')
		}

		console.log('🎥 [joinConference] Подключение к конференции:', conferenceId)

		try {
			// Пытаемся отметить присутствие через API (опционально)
			const conferencesStore = useConferencesStore()

			// Проверяем, есть ли метод joinConference в store
			if (typeof conferencesStore.joinConference === 'function') {
				await conferencesStore.joinConference(conferenceId)
				console.log('✅ [joinConference] Присутствие отмечено')
			}
		} catch (error) {
			// Если endpoint не существует или вернул ошибку - логируем и продолжаем
			console.warn(
				'⚠️ [joinConference] Не удалось отметить присутствие:',
				error.message
			)
			// НЕ выбрасываем ошибку, просто продолжаем переход
		}

		// В любом случае переходим на страницу конференции
		console.log(
			'🚀 [joinConference] Переход на страницу конференции:',
			conferenceId
		)
		router.push(`/conference/${conferenceId}`)
	}

	return {
		conferences,
		loading,
		includePast,
		conferencesWithColors,
		groupedConferences,
		loadConferences,
		togglePast,
		joinConference,
	}
}
